import { cva } from "class-variance-authority";
import { cx } from "class-variance-authority";
import { motion } from "motion/react";
import React, { useId } from "react";

import { Button } from "../Button/Button";
import type { ButtonProps } from "../Button/Button";

import {
  buttonSwitchContainer,
  switchBtn,
  switchBtnLabel,
  activeBtn,
  activeBtnBg,
  containerSizeDefault,
  containerSizeMd,
  containerSizeSm,
  containerSizeXs,
  containerSizeXxs,
} from "../ButtonSwitch/ButtonSwitch.module.scss";

const containerVariants = cva(buttonSwitchContainer, {
  variants: {
    size: {
      default: containerSizeDefault,
      md: containerSizeMd,
      sm: containerSizeSm,
      xs: containerSizeXs,
      xxs: containerSizeXxs,
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const ButtonSwitch = ({
  activeIndex,
  onChange,
  buttons,
  className,
  size,
  disabled,
  buttonClassName,
}: {
  activeIndex: number;
  onChange: (index: number) => void;
  buttons: {
    label: string;
    action?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
  }[];
  className?: string;
  size?: ButtonProps["size"];
  disabled?: boolean;
  buttonClassName?: string;
}) => {
  const uid = useId(); // unique per instance

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (activeIndex === index) {
      // do action
      buttons[index].action?.(e);
      return;
    }
    onChange(index);
  };

  return (
    <div className={cx(containerVariants({ size }), className)}>
      {buttons.map((button, index) => (
        <Button
          key={`switch-btn-${index}`}
          variant="noStyle"
          size={size}
          onClick={(e) => handleClick(e, index)}
          className={cx(
            switchBtn,
            { [activeBtn]: index === activeIndex },
            buttonClassName,
          )}
          disabled={disabled || button.disabled}
        >
          {activeIndex === index && (
            <motion.div
              layoutId={`active-bg-${uid}`}
              className={activeBtnBg}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <div className={switchBtnLabel}>{button.label}</div>
        </Button>
      ))}
    </div>
  );
};

export { ButtonSwitch };
